import type { ReactNode } from 'react';

interface SectionHeadProps {
  kicker: string;
  title: string;
  desc?: string;
  link?: string;
  dark?: boolean;
}

export default function SectionHead({ kicker, title, desc, link, dark }: SectionHeadProps) {
  return (
    <div className="mb-6 md:mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        <div
          className={`flex items-center gap-2 text-xs font-bold section-kicker ${
            dark ? 'text-gold-300' : 'text-flame-600'
          }`}
        >
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${dark ? 'bg-gold-400' : 'bg-flame-500'}`} />
          {kicker}
        </div>
        <h2
          className={`mt-3 font-display text-2xl md:text-4xl font-black tracking-tight ${
            dark ? 'text-paper-50' : 'text-ink-950'
          }`}
        >
          {title}
        </h2>
        {desc ? (
          <p className={`mt-3 max-w-2xl text-sm leading-6 ${dark ? 'text-paper-200/80' : 'text-ink-600'}`}>
            {desc}
          </p>
        ) : null}
      </div>
      {link ? (
        <a
          href={link}
          className="group inline-flex shrink-0 items-center gap-1.5 text-sm font-bold whitespace-nowrap cursor-pointer text-flame-600 hover:text-flame-700"
        >
          查看全部
          <i className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-1" />
        </a>
      ) : null}
    </div>
  );
}

interface PillProps {
  children: ReactNode;
  active?: boolean;
  tone?: 'flame' | 'gold' | 'ink';
  onClick?: () => void;
}

export function TonePill({ children, active, tone = 'ink', onClick }: PillProps) {
  const base =
    'inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer';
  const tones = {
    ink: active
      ? 'bg-ink-950 text-paper-50'
      : 'bg-paper-50 text-ink-700 border border-paper-200 hover:border-ink-900 hover:text-ink-950',
    flame: active
      ? 'bg-flame-500 text-white'
      : 'bg-paper-50 text-ink-700 border border-paper-200 hover:border-flame-400 hover:text-flame-700',
    gold: active
      ? 'bg-gold-500 text-ink-950'
      : 'bg-paper-50 text-ink-700 border border-paper-200 hover:border-gold-500 hover:text-gold-700',
  };
  return (
    <button type="button" onClick={onClick} className={`${base} ${tones[tone]}`}>
      {children}
    </button>
  );
}